<template>
  <component
    :is="path || (link && link.path) ? 'a' : 'article'"
    :class="['zg-article-preview', {
      [`zg-article-preview--background-${background}`]: background && !['large', 'medium'].includes(gridType),
      'zg-article-preview__type-medium-large': ['large', 'medium'].includes(gridType),
      'zg-article-preview--small': itemSize === 'small',
      'zg-article-preview--medium': itemSize !== 'small' && gridType === 'medium',
      'zg-article-preview--no-rounded-borders': noRoundedBorders
    }]"
    :href="path || (link && link.path)"
  >
    <zg-image
      v-if="hero && hero.src"
      :src="hero.src"
      :sources="hero.sources"
      :title="hero.description"
      :alt="hero.alt"
      :lazy-load="lazyLoad"
    />

    <div class="zg-article-preview__main-container">
      <div class="zg-article-preview__main">
        <zg-label
          v-if="category && category.categoryName"
          :text="category.categoryName"
          :size="itemSize === 'small' ? 'small' : 'medium'"
        />

        <div class="zg-article-preview__headline">
          <component
            :is="itemSize === 'small' ? 'h5' : 'h4'"
            v-if="headline"
            v-html="headline"
          />
          <zg-icon
            v-if="path || (link && link.path)"
            icon="forward"
            size="small"
          />
        </div>

        <div
          v-if="preamble"
          class="zg-article-preview__preamble"
          v-html="preamble"
        />
      </div>
      <div
        v-if="publishedAt || author"
        class="zg-article-preview__footer"
      >
        <span>{{ publishedAt }}{{ publishedAt && author ? ', ' : null }}</span>
        <span v-if="author">{{ `${author.firstName} ${author.surname}` }}</span>
      </div>
    </div>
  </component>
</template>

<script>
  import ZgImage from '../ZgImage/ZgImage.vue'
  import ZgLabel from '../ZgLabel/ZgLabel.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'

  export default {
    name: 'ZgArticlePreview',
    components: {
      ZgLabel,
      ZgIcon,
      ZgImage
    },
    props: {
      background: {
        type: String,
        required: false,
        default: 'white',
        validator: value => !value || [
          'transparent',
          'beige',
          'white'
        ].includes(value)
      },
      itemSize: {
        type: String,
        required: false,
        default: 'medium',
        validator: value => [
          'large',
          'medium',
          'small'
        ].includes(value)
      },
      noRoundedBorders: {
        type: Boolean,
        require: false,
        default: false
      },
      gridType: {
        type: String,
        default: 'grid',
        validator: value => [
          'large',
          'medium',
          'grid'
        ].includes(value)
      },
      hero: {
        type: Object,
        default: null
      },
      category: {
        type: Object,
        default: null,
        required: false,
        validator: category => !category || category.categoryName
      },
      headline: {
        type: String,
        required: false,
        default: null
      },
      preamble: {
        type: String,
        required: false,
        default: ''
      },
      author: {
        type: Object,
        default: null,
        required: false,
        validator: author => !author || (author.firstName && author.surname)
      },
      publishedAt: {
        type: String,
        required: false,
        default: ''
      },
      path: {
        type: String,
        default: ''
      },
      link: {
        type: Object,
        required: false,
        default: () => { }
      },
      lazyLoad: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      maxCharacters: {
        headline: 55,
        preamble: 95
      }
    })
  }
</script>

<style lang="scss" scoped>
.zg-article-preview {
  position: relative;
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: radius(large);
  border: none;

  &__main-container {
    height: 100%;
    width: 100%;
    padding: rhythm(base);
    gap: rem(20);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__main {
    display: grid;
    gap: rem(20);
    justify-items: start;
    height: fit-content;
  }

  &__headline {
    display: grid;
    grid-template-columns: 1fr auto;
    width: 100%;
    gap: rhythm(xsmall);
    justify-content: space-between;
    align-items: center;

    svg {
      width: rem(20);
      height: rem(20);
    }
  }

  &__preamble {
    @include type-body-m;

    -webkit-line-clamp: 3; /* number of lines to show */
            line-clamp: 3;
  }

  &__footer {
    @include type-body-s;

    margin: 0;
    color: color(gray-500);
  }

  h4 {
    @include type-title-m;

    -webkit-line-clamp: 2; /* number of lines to show */
            line-clamp: 2;
  }

  h5 {
    @include type-title-s;

    -webkit-line-clamp: 2; /* number of lines to show */
            line-clamp: 2;
  }

  &__preamble,
  &__footer,
  h4,
  h5 {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    margin: 0;

    &::before,
    &::after {
        content: '';
        display: block;
        height: 0;
        width: 0;
    }

    &::before{
        margin-top:-1px;
    }

    &::after{
        margin-bottom:-2px;
    }
  }

  ::v-deep img {
    width: 100%;
    height: 100%;
    max-height: rem(212);
    object-fit: cover;
    object-position: 50% 10%;
    border-radius: radius(large) radius(large) 0 0;
  }

  &:focus {
    box-shadow: box-shadow(focus);
    outline: none;
  }

  &:focus:not(:focus-visible) {
    box-shadow: none;
  }

  @include mq(large) {
    width: rem(344);
    height: 100%;
  }
}

a {
  &:hover {
    .zg-article-preview__headline,
    svg {
      color: color(brand-500);
    }
  }
}

::v-deep .zg-label--size-small {
  margin: 0;
}

// ? ----------------------------------------------------------------------
// background
// ? ----------------------------------------------------------------------

.zg-article-preview--background-beige {
  background-color: color(beige);
}

.zg-article-preview--background-white {
  background-color: color(white);
}

.zg-article-preview__type-medium-large {
  background-color: transparent;

  .zg-article-preview__main {
    gap: rem(20);
  }

  .zg-article-preview__preamble {
    margin: 0;

    ::v-deep p {
      display: inline;
    }
  }

  .zg-article-preview__main-container {
    height: unset;
    padding: rhythm() 0 0 0;
  }

  ::v-deep img {
    width: 100%;
    height: 100%;
    max-height: rem(425);
    object-fit: cover;
    object-position: 50% 10%;
    border-radius: radius(large);
  }

  @include mq(large) {
    max-width: rem(688);
    width: unset;
  }
}

// ? ----------------------------------------------------------------------
// size
// ? ----------------------------------------------------------------------

.zg-article-preview--small {
  flex-direction: row;
  align-items: flex-start;
  gap: rhythm(small);

  .zg-article-preview {
    &__main-container {
      gap: rhythm(small);
      padding: 0;
    }

    &__main {
      gap: rhythm(small);
    }

    &__read-more {
      margin: 0;
    }

    &__preamble {
      @include type-body-s;

      margin: 0;

      p {
        display: inline;
      }
    }
  }

  picture {
    display: block;
  }

  ::v-deep img {
    width: rem(64);
    min-width: rem(64);
    height: rem(64);
    min-height: rem(64);
    object-fit: cover;
    object-position: 50% 10%;
    border-radius: radius(half);
  }
}

.zg-article-preview--medium {
  max-width: unset;

  ::v-deep img {
    width: 100%;
    height: 100%;
    max-height: rem(234);
    object-fit: cover;
    object-position: 50% 10%;
  }
}

.zg-article-preview--no-rounded-borders {
  ::v-deep img {
    border-radius: unset;
  }
}
</style>
